import { buildPrompt } from '@/ai/utils/promptBuilder'
import { createSystemMessage, createUserMessage } from '@/ai/utils/promptUtils'
import { examples, getEventExample } from '@/ai/prompts/common/examples'
import { getJsonArrayOutputFormatting } from '@/ai/prompts/common/formatting'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { transformWorkflow } from '@/ai/utils/transformWorkflow'

export const buildWorkflowWriteModelsPrompt = ({ workflow, cardTypes }) => {
  const messages = []

  const systemContent = buildPrompt([systemMessages.EVENT_MODELING_DDD_EXPERT])
  messages.push(createSystemMessage(systemContent))

  const promptSections = []

  promptSections.push({
    title: 'Request',
    content: `
      For each domain event, create a set of input fields. You are designing simple input forms
      (or mockups or wireframes) with data fields to be filled in and submitted by the actor (or role),
      in order to invoke a command on an aggregate, for each step of the process.
      Each data field is an input field that the user (or system, if automated) can fill in.
      The form reflects the information requested from the actor in order to invoke the command on the aggregate.
      The number of input fields per step can vary, but it is usually between 4 and 8.
      In most of the cases the generated data fields should match with the data fields of the entity and include
      the primary key field (id) of the entity. A data field name must be in camel case format (e.g., 'productName'
      instead of 'Product Name'). A data field name must be at most 30 characters long.
    `
  })

  promptSections.push({
    title: 'Workflow Description',
    content: `\`${workflow.aiDescription}\`` || 'N/A'
  })

  const { events, entities, eventIdToLongIdMap } = transformWorkflow(workflow)

  promptSections.push({
    title: 'Current Workflow (JSON format)',
    contentType: 'codeblock',
    language: 'json',
    content: JSON.stringify({ domainEvents: events })
  })

  promptSections.push({
    title: 'Existing Entities (Optional): Use this information if applicable.',
    contentType: 'codeblock',
    language: 'json',
    content: JSON.stringify({ entities })
  })

  const formattingContent = `The resulting array must contain ${workflow.eventsJson.length} items.`

  const options = {
    shouldAddCards: false,
    shouldAddDataModels: true
  }

  const exampleData1 = {
    id: 1,
    commandExample: examples.commandExampleCreateOrder
  }
  const exampleData2 = {
    id: 2,
    commandExample: examples.commandExampleDeliverOrder
  }

  const eventsExample = [
    getEventExample(exampleData1, cardTypes, options),
    getEventExample(exampleData2, cardTypes, options)
  ]

  promptSections.push({
    title: 'Very important considerations',
    contentType: 'bullet',
    content: [
      'Name the fields as they would be named on an entity or in the database in a real system, use camel case format',
      `Use the same field names across the events, if the same information is requested in different steps,
       for example "customerName" should be used in all domain events, not "customerName" in one event
       and "newCustomerName" in another event`,
      'Name the id field only id and not entityNameId'
    ]
  })

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: getJsonArrayOutputFormatting(JSON.stringify(eventsExample), formattingContent)
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return { messages, eventIdToLongIdMap }
}
